import { Link } from "react-router-dom";
import { ArrowRight, Phone, Cloud, Wifi, Router, Server, Shield, Headphones, MapPin, Zap, LineChart } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050914] text-slate-100 relative">
      {/* subtle background layers */}
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="absolute top-0 inset-x-0 h-[720px] bg-gradient-to-b from-cyan-500/[0.08] via-transparent to-transparent pointer-events-none" />

      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#050914]/80 backdrop-blur-xl border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <img src="/brand/steele-cloud-header.png" alt="Steele Cloud — Cloud Hosting, NBN, VoIP, PBX Systems" className="h-11 md:h-12 w-auto object-contain" />
          </Link>
          <nav className="flex items-center gap-6">
            <a href="#products" data-testid="nav-products" className="hidden md:inline text-sm text-slate-400 hover:text-white transition-colors">Products</a>
            <a href="#why" data-testid="nav-why" className="hidden md:inline text-sm text-slate-400 hover:text-white transition-colors">Why Steele Cloud</a>
            <a href="#contact" data-testid="nav-contact" className="hidden md:inline text-sm text-slate-400 hover:text-white transition-colors">Become a partner</a>
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm font-medium bg-brand-gradient text-slate-950 px-5 py-2 rounded-full hover:brightness-110 transition-all"
            >
              Partner sign in
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24 relative">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/70 backdrop-blur px-3 py-1 text-xs font-medium text-slate-300 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-cyan" />
                Australian-owned · Regional-focused · Reseller-friendly
              </span>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight font-medium mb-8">
                Wholesale <span className="text-brand-gradient">connectivity, voice &amp; cloud</span> for Australian resellers.
              </h1>
              <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl mb-10">
                One supplier for VoIP, NBN, hosted PBX, link bonding and cloud hosting.
                No lock-in, human support, and a live partner portal that tracks every commission you earn.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a
                  href="#contact"
                  data-testid="hero-cta-partner"
                  className="inline-flex items-center gap-2 bg-brand-gradient text-slate-950 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110 hover:shadow-[0_10px_40px_-8px_rgba(40,208,255,0.5)]"
                >
                  Become a partner <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#products"
                  data-testid="hero-cta-products"
                  className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 hover:bg-slate-800 px-6 py-3.5 rounded-full font-medium text-sm text-slate-200 transition-colors"
                >
                  Explore products
                </a>
              </div>

              <div className="grid grid-cols-3 gap-8 mt-16 max-w-lg">
                <MiniStat number="24/7" label="AU-based support" />
                <MiniStat number="< 24h" label="Typical provisioning" />
                <MiniStat number="0" label="Lock-in contracts" />
              </div>
            </div>

            {/* Hero visual — product stack */}
            <div className="md:col-span-5 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <div className="relative">
                <div className="rounded-2xl bg-slate-900/70 backdrop-blur border border-slate-800 ring-brand p-6 space-y-3">
                  <HeroProductRow icon={<Phone className="w-4 h-4" />} label="Hosted VoIP · 3CX PBX" tag="Per-extension" />
                  <HeroProductRow icon={<Wifi className="w-4 h-4" />} label="Business NBN" tag="Business plans" accent />
                  <HeroProductRow icon={<Cloud className="w-4 h-4" />} label="Cloud VPS / Hosting" tag="Managed" />
                  <HeroProductRow icon={<Router className="w-4 h-4" />} label="Link Bonding · SD-WAN" tag="Multi-carrier" />
                  <HeroProductRow icon={<Server className="w-4 h-4" />} label="PBX hardware · Handsets" tag="Wholesale" />
                </div>
                <div className="absolute -bottom-6 -right-6 rounded-xl bg-slate-950 border border-cyan-500/30 p-4 shadow-xl hidden md:block glow-brand">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">Partner Portal</div>
                  <div className="font-display text-sm font-medium text-cyan-300">Live commission tracking</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="max-w-7xl mx-auto px-6 py-24 relative">
        <div className="max-w-3xl mb-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-4">Product suite</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mb-6">
            Everything your customers need. From one supplier.
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Voice, data, hosting and hardware — pre-integrated, wholesale-priced, ready to resell under your brand.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProductCard
            icon={<Phone className="w-5 h-5" />}
            title="Hosted VoIP"
            body="SIP trunks, hosted 3CX instances, DID number blocks, and porting. Handset provisioning included. Per-extension monthly billing across your whole customer base."
            tags={["3CX partner", "AU numbers", "DID porting"]}
          />
          <ProductCard
            icon={<Server className="w-5 h-5" />}
            title="PBX Systems"
            body="Yealink and Grandstream handsets at wholesale, on-prem 3CX and cloud PBX rollouts. Full config and provisioning done — you just ship the hardware."
            tags={["Yealink", "Grandstream", "3CX"]}
          />
          <ProductCard
            icon={<Wifi className="w-5 h-5" />}
            title="NBN Resale"
            body="Business-grade NBN across FTTP, FTTC, FTTN and Fixed Wireless. Static IPs available, priority ticketing to nbn co, and human support when it breaks."
            tags={["FTTP", "Static IP", "Business SLAs"]}
          />
          <ProductCard
            icon={<Router className="w-5 h-5" />}
            title="Link Bonding & SD-WAN"
            body="Bond multiple carriers for resilience — perfect for cafes, motels, remote sites and anywhere a single NBN service isn't reliable enough."
            tags={["Multi-WAN", "Failover", "4G backup"]}
          />
          <ProductCard
            icon={<Cloud className="w-5 h-5" />}
            title="Cloud Hosting"
            body="AU-hosted VPS, managed WordPress, and dedicated servers. Daily backups, DDoS protection and Let's Encrypt built in. White-label DNS for reseller branding."
            tags={["AU-hosted", "Managed", "Backups"]}
          />
          <ProductCard
            icon={<Shield className="w-5 h-5" />}
            title="Backup & DR"
            body="Off-site backups, immutable retention, and disaster-recovery hot-spare configurations for customers running mission-critical loads."
            tags={["Immutable", "3-2-1", "Hot spare"]}
          />
        </div>
      </section>

      {/* Why Steele Cloud */}
      <section id="why" className="relative">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-4">Why partner with us</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mb-6">
                A wholesale partner that treats you like one.
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-10">
                We do the carrier relationships, the after-hours support and the boring stuff.
                You keep the customer, keep the margin, and see every commission accrued in your portal — in real time.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <BigStat value="One" label="Supplier, one bill" />
                <BigStat value="Live" label="Commission tracking" />
              </div>
            </div>
            <div className="space-y-4">
              <ReasonRow icon={<LineChart className="w-4 h-4" />} title="Live commission portal"
                body="Every referred payment lands in your dashboard within seconds. Tiered rates per product line. Monthly PDF statements auto-generated." />
              <ReasonRow icon={<MapPin className="w-4 h-4" />} title="Regional-Australian by default"
                body="We built Steele Cloud out of the country. If your customers are outside the metros, we're the supplier who's already there." />
              <ReasonRow icon={<Headphones className="w-4 h-4" />} title="Humans, not tickets"
                body="Direct-dial mobile numbers for your engineers when something's on fire. No offshore call centres, no L1 triage loop." />
              <ReasonRow icon={<Zap className="w-4 h-4" />} title="Fast provisioning"
                body="Most VoIP and hosting services are live within the hour. NBN inside the standard nbn co window. No sales gates between quote and delivery." />
            </div>
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section id="contact" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border border-cyan-500/20 p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-[0.15]" />
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px]" />
          <div className="relative max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-4">Become a partner</div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tight font-medium mb-6 text-white">
              Reseller pricing, on request.
            </h3>
            <p className="text-slate-400 text-lg mb-8">
              We onboard IT resellers, MSPs and telcos across Australia. Get in touch for a quote and a walkthrough of the partner portal.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="mailto:partners@steelecloud.com.au?subject=Steele%20Cloud%20partner%20enquiry"
                data-testid="cta-email-partners"
                className="inline-flex items-center gap-2 bg-brand-gradient text-slate-950 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
              >
                Email partners@steelecloud.com.au <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/login"
                data-testid="cta-partner-signin"
                className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 hover:bg-slate-800 px-6 py-3.5 rounded-full font-medium text-sm text-slate-200 transition-colors"
              >
                Existing partner sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <img src="/brand/steele-cloud-header.png" alt="Steele Cloud" className="h-11 w-auto mb-5" />
            <p className="text-sm text-slate-500 max-w-md">
              Wholesale VoIP, NBN, cloud hosting and connectivity — for IT resellers, MSPs and communications businesses across Australia.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-400 font-semibold mb-3">Products</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="#products" className="hover:text-white transition-colors">Hosted VoIP</a></li>
              <li><a href="#products" className="hover:text-white transition-colors">Business NBN</a></li>
              <li><a href="#products" className="hover:text-white transition-colors">Cloud hosting</a></li>
              <li><a href="#products" className="hover:text-white transition-colors">Link bonding</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-400 font-semibold mb-3">Partners</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/login" className="hover:text-white transition-colors">Partner sign in</Link></li>
              <li><a href="mailto:partners@steelecloud.com.au" className="hover:text-white transition-colors">Become a partner</a></li>
              <li><a href="#why" className="hover:text-white transition-colors">Why Steele Cloud</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800/60">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-slate-500 flex flex-col md:flex-row justify-between gap-3">
            <div>© 2026 Steele Cloud. Cloud Hosting · NBN · VoIP · PBX Systems.</div>
            <div>Australian-owned &amp; operated.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MiniStat({ number, label }) {
  return (
    <div className="border-l border-cyan-500/40 pl-3">
      <div className="font-mono text-2xl font-medium text-slate-100">{number}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function HeroProductRow({ icon, label, tag, accent }) {
  return (
    <div className={`flex items-center justify-between rounded-lg border ${accent ? "border-cyan-500/40 bg-cyan-500/[0.06]" : "border-slate-800 bg-slate-950/40"} px-3 py-2.5`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${accent ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-800 text-slate-400"}`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-slate-200">{label}</span>
      </div>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{tag}</span>
    </div>
  );
}

function ProductCard({ icon, title, body, tags }) {
  return (
    <div className="group rounded-xl bg-slate-900/60 backdrop-blur border border-slate-800 p-6 transition-all hover:-translate-y-[2px] hover:border-cyan-500/40 hover:bg-slate-900">
      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 text-cyan-400 grid place-items-center mb-4 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors">
        {icon}
      </div>
      <h3 className="font-display text-xl font-medium mb-2 tracking-tight text-white">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-4">{body}</p>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-wider font-semibold text-cyan-300/80 border border-cyan-500/20 bg-cyan-500/[0.05] px-2 py-0.5 rounded-full">{t}</span>
        ))}
      </div>
    </div>
  );
}

function BigStat({ value, label }) {
  return (
    <div>
      <div className="font-display text-4xl font-medium text-brand-gradient">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function ReasonRow({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 flex items-start gap-4 hover:border-cyan-500/30 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-cyan-500/10 text-cyan-400 grid place-items-center shrink-0 border border-cyan-500/20">
        {icon}
      </div>
      <div>
        <h4 className="font-display text-lg text-white tracking-tight mb-1">{title}</h4>
        <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
